import React, { useState, useEffect } from 'react'
import SimpleNextJSChart from '@/components/charts/SimpleNextJSChart'
import { apiService } from '@/services/api'

interface SalesPrediction {
  date: string
  predictedSales: number
  confidence: number
  trend: 'increasing' | 'decreasing' | 'stable'
}

interface InventoryOptimization {
  productId: string
  productName: string
  sku: string
  currentStock: number
  optimalMinStock: number
  optimalMaxStock: number
  suggestion: string
  priority: 'high' | 'medium' | 'low'
  potentialSavings: number
}

/** AI Insights section for use inside Reports (and optionally as standalone page). */
export const AIInsightsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState('predictions')
  const [loading, setLoading] = useState(false)
  const [salesPredictions, setSalesPredictions] = useState<SalesPrediction[]>([])
  const [inventoryOptimizations, setInventoryOptimizations] = useState<InventoryOptimization[]>([])

  const tabs = [
    { id: 'predictions', label: 'Sale Forecasts', icon: '📈' },
    { id: 'inventory', label: 'Stock Strategy', icon: '📦' },
    { id: 'customers', label: 'Reader Profiles', icon: '👥' },
    { id: 'pricing', label: 'Price Optimization', icon: '💰' }
  ]

  const fetchData = async () => {
    setLoading(true)
    try {
      if (activeTab === 'predictions') {
        const res = await apiService.get<any>('/ai/predictions/sales?days=7')
        if (res.success) setSalesPredictions(res.predictions || [])
      } else if (activeTab === 'inventory') {
        const res = await apiService.get<any>('/ai/optimization/inventory')
        if (res.success) setInventoryOptimizations(res.suggestions || [])
      }
    } catch (error) {
      console.error(`Failed to fetch ${activeTab} data:`, error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [activeTab])

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold esales-text font-display">AI Insights</h2>
          <p className="text-muted-foreground text-sm mt-1">Neural insights powered by transaction data.</p>
        </div>
        <div className="bg-primary/10 border border-primary/20 px-4 py-2 rounded-full flex items-center space-x-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
          </span>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">Model Online</span>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 bg-muted/20 p-1.5 rounded-2xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all font-bold text-sm ${activeTab === tab.id ? 'bg-primary text-white shadow-lg' : 'text-muted-foreground hover:bg-muted/40'}`}
          >
            <span className="text-base">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 glass-card">
            <div className="animate-spin text-5xl mb-4 text-primary">🧠</div>
            <p className="font-bold text-muted-foreground animate-pulse">Running neural analysis...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {activeTab === 'predictions' && (
              <>
                <div className="glass-card">
                  <h3 className="text-xl font-bold mb-6 esales-text">7-Day Sales Forecast</h3>
                  <div className="h-84">
                    <SimpleNextJSChart
                      data={salesPredictions.map(p => ({ x: new Date(p.date).toLocaleDateString(), y: p.predictedSales, label: `Expected: Rs.${p.predictedSales}` }))}
                      type="line"
                      color="var(--primary)"
                    />
                  </div>
                </div>
                <div className="glass-card">
                  <h3 className="text-xl font-bold mb-6 esales-text">Confidence Matrix</h3>
                  <div className="space-y-4">
                    {salesPredictions.length === 0 && <p className="text-muted-foreground text-center py-6">No prediction data yet.</p>}
                    {salesPredictions.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-muted/20 rounded-xl">
                        <div>
                          <p className="font-bold text-xs uppercase tracking-widest text-muted-foreground">{new Date(p.date).toDateString()}</p>
                          <p className="text-xl font-black">Rs.{p.predictedSales.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${p.trend === 'increasing' ? 'text-green-400 bg-green-400/10' : 'text-blue-400 bg-blue-400/10'}`}>
                            {p.trend}
                          </span>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">{p.confidence}% CONF</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'inventory' && (
              <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {inventoryOptimizations.length === 0 && <p className="col-span-full text-muted-foreground text-center py-10">No optimization suggestions yet.</p>}
                {inventoryOptimizations.map((item, i) => (
                  <div key={i} className="glass-card flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-black text-lg leading-tight uppercase tracking-tight">{item.productName}</h4>
                        <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase ${item.priority === 'high' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}>
                          {item.priority}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-6">
                        <div className="bg-muted/10 p-2 rounded-lg text-center">
                          <p className="text-[10px] uppercase text-muted-foreground font-bold">Current</p>
                          <p className="text-lg font-black">{item.currentStock}</p>
                        </div>
                        <div className="bg-primary/5 p-2 rounded-lg text-center border border-primary/20">
                          <p className="text-[10px] uppercase text-primary font-bold">Optimal</p>
                          <p className="text-lg font-black text-primary">{item.optimalMinStock}</p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-border">
                      <p className="text-xs font-bold text-muted-foreground mb-3 flex items-center">
                        <span className="mr-2">💡</span> {item.suggestion.replace('_', ' ').toUpperCase()}
                      </p>
                      <button className="w-full py-2 bg-primary text-white rounded-lg text-xs font-black tracking-widest hover:opacity-90 transition-opacity">
                        GENERATE PO
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(activeTab === 'customers' || activeTab === 'pricing') && (
              <div className="col-span-full py-20 text-center glass-card">
                <div className="text-5xl mb-4">🔬</div>
                <h3 className="text-2xl font-bold mb-2">Neural Engine Training</h3>
                <p className="text-muted-foreground max-w-md mx-auto italic">
                  This analysis module requires 30 days of continuous transaction history to generate high-confidence profiles. Please continue using the terminal to train the model.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
